import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { classOf } from "@/lib/pls";

const popupHtml = (a) => {
  const c = classOf(a);
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));
  return `<div style="font-family:Manrope,sans-serif;min-width:180px">
    <div style="font-weight:700;color:#0A1B2E;font-size:14px">${esc(a.name)}</div>
    <div style="margin-top:4px;font-size:12px;color:#475569">${esc(a.municipality || "")}${a.state ? `, ${esc(a.state)}` : ""}</div>
    <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;font-size:11px">
      <span style="background:${c.color};color:#fff;border-radius:999px;padding:2px 8px">${esc(c.label)}</span>
      <span style="background:#F4F9FD;border-radius:999px;padding:2px 8px;font-family:monospace">${esc(a.icao || "—")}</span>
      ${a.iata ? `<span style="background:#F4F9FD;border-radius:999px;padding:2px 8px;font-family:monospace">${esc(a.iata)}</span>` : ""}
      ${a.ersa ? `<span style="background:#E0F2FE;color:#075985;border-radius:999px;padding:2px 8px">ERSA</span>` : ""}
    </div>
    <div style="margin-top:8px;font-size:11px;color:#64748B;font-family:monospace">${Number(a.lat).toFixed(5)}, ${Number(a.lng).toFixed(5)}</div>
  </div>`;
};

export const PlsMap = ({ rows, focus }) => {
  const el = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const markers = useRef({});

  useEffect(() => {
    const m = L.map(el.current, { preferCanvas: true, zoomControl: true }).setView([-26.5, 134], 4);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 16, attribution: "Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors",
    }).addTo(m);
    map.current = m;
    layer.current = L.layerGroup().addTo(m);
    return () => {
      m.remove();
    };
  }, []);

  useEffect(() => {
    const g = layer.current;
    if (!g) return;
    g.clearLayers();
    markers.current = {};
    rows.forEach((a) => {
      const c = classOf(a).color;
      const mk = L.circleMarker([Number(a.lat), Number(a.lng)], { radius: a.aero_class === "major" ? 7 : 5, color: "#fff", weight: 1, fillColor: c, fillOpacity: 0.9 })
        .bindPopup(popupHtml(a));
      mk.addTo(g);
      markers.current[a.airfield_id] = mk;
    });
  }, [rows]);

  useEffect(() => {
    if (!focus || !map.current) return;
    map.current.flyTo([Number(focus.lat), Number(focus.lng)], 12, { duration: 1.2 });
    const t = setTimeout(() => markers.current[focus.airfield_id]?.openPopup(), 1250);
    return () => clearTimeout(t);
  }, [focus]);

  return <div ref={el} className="h-[420px] w-full overflow-hidden rounded-[28px] border border-sky-100 sm:h-[560px]" data-testid="pls-map" />;
};
